/**
 * Image Cache Debug Utilities
 * Tools to verify and debug image caching functionality
 */

import {
  isImageCached,
  getCachedImage,
  getCacheSize,
  clearCache,
} from '@/services/imageCacheService';

/**
 * Verify if an image is cached
 */
export async function verifyImageCache(url: string): Promise<{
  isCached: boolean;
  blobUrl: string | null;
  cacheSize: number;
}> {
  const isCached = await isImageCached(url);
  const blobUrl = isCached ? await getCachedImage(url) : null;
  const cacheSize = await getCacheSize();

  return {
    isCached,
    blobUrl,
    cacheSize,
  };
}

/**
 * Test cache functionality for a list of image URLs
 */
export async function testImageCache(urls: string[]): Promise<{
  total: number;
  cached: number;
  notCached: number;
  results: Array<{
    url: string;
    isCached: boolean;
  }>;
}> {
  const results = await Promise.all(
    urls.map(async url => ({
      url,
      isCached: await isImageCached(url),
    }))
  );

  const cached = results.filter(r => r.isCached).length;
  const notCached = results.length - cached;

  return {
    total: urls.length,
    cached,
    notCached,
    results,
  };
}

/**
 * Log cache statistics to console
 */
export async function logCacheStats(): Promise<void> {
  const cacheSize = await getCacheSize();
  const canUseCache = typeof window !== 'undefined' && 'caches' in window;

  console.group('📊 Image Cache Statistics');
  console.log('Cache API Available:', canUseCache);
  console.log('Total Cached Images:', cacheSize);
  console.log('Cache Name:', 'nexgo-images-v1');

  if (canUseCache && typeof window !== 'undefined') {
    try {
      const cache = await caches.open('nexgo-images-v1');
      const keys = await cache.keys();
      console.log('Cache Keys:', keys.length);
      console.log(
        'Sample URLs:',
        keys.slice(0, 5).map(k => k.url)
      );
    } catch (error) {
      console.error('Error accessing cache:', error);
    }
  }

  console.groupEnd();
}

/**
 * Monitor image loading and cache hits
 * Call this in browser console to monitor cache performance
 */
export function monitorImageCache(): void {
  if (typeof window === 'undefined') {
    console.warn('monitorImageCache can only be used in browser');
    return;
  }

  let cacheHits = 0;
  let cacheMisses = 0;
  let totalRequests = 0;

  // Override fetch to monitor cache usage
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const url = args[0] as string;
    totalRequests++;

    // Check if this is an image request
    if (
      typeof url === 'string' &&
      (url.match(/\.(jpg|jpeg|png|gif|webp|svg)/i) ||
        url.includes('image') ||
        url.includes('photo'))
    ) {
      const isCached = await isImageCached(url);
      if (isCached) {
        cacheHits++;
        console.log('✅ Cache HIT:', url.substring(0, 100));
      } else {
        cacheMisses++;
        console.log('❌ Cache MISS:', url.substring(0, 100));
      }
    }

    return originalFetch.apply(this, args);
  };

  console.log('🔍 Image cache monitoring enabled');
  console.log('To view stats, call: window.getCacheStats()');

  // Expose stats function
  (window as Window & { getCacheStats?: () => void }).getCacheStats = () => {
    console.group('📊 Cache Performance Stats');
    console.log('Total Requests:', totalRequests);
    console.log('Cache Hits:', cacheHits);
    console.log('Cache Misses:', cacheMisses);
    console.log(
      'Hit Rate:',
      totalRequests > 0
        ? `${((cacheHits / totalRequests) * 100).toFixed(2)}%`
        : '0%'
    );
    console.groupEnd();
  };

  // Expose stop function
  (
    window as Window & { stopCacheMonitoring?: () => void }
  ).stopCacheMonitoring = () => {
    window.fetch = originalFetch;
    console.log('🛑 Cache monitoring stopped');
  };
}

/**
 * Clear all cached images (for testing)
 */
export async function clearAllCache(): Promise<boolean> {
  const cleared = await clearCache();
  console.log('🗑️ Cache cleared:', cleared);
  return cleared;
}

/**
 * Verify cache is working by testing a specific image
 */
export async function verifyCacheWorking(imageUrl: string): Promise<{
  success: boolean;
  message: string;
  details: {
    initiallyCached: boolean;
    afterLoadCached: boolean;
    cacheSize: number;
  };
}> {
  console.group('🔍 Verifying Image Cache');

  // Check initial state
  const initiallyCached = await isImageCached(imageUrl);
  const initialCacheSize = await getCacheSize();

  console.log('1. Initial State:');
  console.log('   - Image cached:', initiallyCached);
  console.log('   - Cache size:', initialCacheSize);

  // Load the image (this should cache it)
  try {
    const response = await fetch(imageUrl, {
      mode: 'cors',
      credentials: 'omit',
    });

    if (response.ok) {
      // Wait a bit for cache to be written
      await new Promise(resolve => setTimeout(resolve, 500));

      // Check if it's now cached
      const afterLoadCached = await isImageCached(imageUrl);
      const afterCacheSize = await getCacheSize();

      console.log('2. After Loading:');
      console.log('   - Image cached:', afterLoadCached);
      console.log('   - Cache size:', afterCacheSize);

      const success = afterLoadCached && afterCacheSize > initialCacheSize;

      console.log('3. Result:');
      console.log(
        '   -',
        success ? '✅ Cache is working!' : '❌ Cache may not be working'
      );

      console.groupEnd();

      return {
        success,
        message: success
          ? 'Cache is working correctly!'
          : 'Cache may not be working. Check browser support.',
        details: {
          initiallyCached,
          afterLoadCached,
          cacheSize: afterCacheSize,
        },
      };
    } else {
      console.error('Failed to load image:', response.status);
      console.groupEnd();
      return {
        success: false,
        message: 'Failed to load image for testing',
        details: {
          initiallyCached,
          afterLoadCached: false,
          cacheSize: initialCacheSize,
        },
      };
    }
  } catch (error) {
    console.error('Error verifying cache:', error);
    console.groupEnd();
    return {
      success: false,
      message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      details: {
        initiallyCached,
        afterLoadCached: false,
        cacheSize: initialCacheSize,
      },
    };
  }
}

// Expose utilities to window for easy console access
if (typeof window !== 'undefined') {
  const imageCacheDebug = {
    verifyImageCache,
    testImageCache,
    logCacheStats,
    monitorImageCache,
    clearAllCache,
    verifyCacheWorking,
  };
  (
    window as Window & { imageCacheDebug?: typeof imageCacheDebug }
  ).imageCacheDebug = imageCacheDebug;
}
